import { useEffect, useState } from "react";
import { toast } from "sonner";
import { apiErr } from "@/lib/api";
import { cachedGet, runOp } from "@/lib/offline";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2 } from "lucide-react";
import { money } from "@/lib/vi";

const ELEC = ["TMoney", "Flooz", "Virement", "Autre électronique"];

function Pick({ value, onChange, options, placeholder, testid }) {
  return (
    <Select value={value || ""} onValueChange={onChange}>
      <SelectTrigger className="mt-1" data-testid={testid}><SelectValue placeholder={placeholder} /></SelectTrigger>
      <SelectContent>{options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
    </Select>
  );
}

function SaleLines({ lines, setLines, stock }) {
  const upd = (i, k, v) => setLines(lines.map((l, j) => (j === i ? { ...l, [k]: v } : l)));
  return (
    <div className="space-y-2">
      {lines.map((l, i) => {
        const row = stock.find((s) => s.article_id === l.article_id);
        return (
          <div key={i} className="grid grid-cols-12 gap-2 items-end rounded-lg border p-2" data-testid={`sale-line-${i}`}>
            <div className="col-span-12 sm:col-span-6"><Label className="text-xs">Article</Label>
              <Pick value={l.article_id} onChange={(v) => upd(i, "article_id", v)} placeholder="Article" testid={`sale-article-${i}`}
                options={stock.filter((s) => s.restant > 0).map((s) => ({ value: s.article_id, label: `${s.designation} — ${s.restant} dispo · ${money(s.prix)}` }))} /></div>
            <div className="col-span-4 sm:col-span-2"><Label className="text-xs">Qté</Label><Input type="number" min="0" className="mt-1" value={l.quantite} onChange={(e) => upd(i, "quantite", Number(e.target.value))} data-testid={`sale-qty-${i}`} /></div>
            <div className="col-span-4 sm:col-span-2"><Label className="text-xs">Remise %</Label><Input type="number" min="0" className="mt-1" value={l.remise} onChange={(e) => upd(i, "remise", Number(e.target.value))} data-testid={`sale-remise-${i}`} /></div>
            <div className="col-span-3 sm:col-span-1 text-xs font-mono text-right pb-2">{row ? money(row.prix * l.quantite * (1 - (l.remise || 0) / 100)) : ""}</div>
            <button type="button" className="col-span-1 pb-2 text-slate-400 hover:text-rose-500" onClick={() => setLines(lines.filter((_, j) => j !== i))}><Trash2 className="h-4 w-4" /></button>
          </div>
        );
      })}
      <Button type="button" variant="outline" size="sm" onClick={() => setLines([...lines, { article_id: "", quantite: 1, remise: 0 }])} data-testid="sale-add-line"><Plus className="h-4 w-4 mr-1" />Article</Button>
    </div>
  );
}

function Payments({ pays, setPays, modes }) {
  const upd = (i, k, v) => setPays(pays.map((p, j) => (j === i ? { ...p, [k]: v } : p)));
  return (
    <div className="space-y-2">
      {pays.map((p, i) => (
        <div key={i} className="grid grid-cols-12 gap-2 items-end" data-testid={`pay-line-${i}`}>
          <div className="col-span-6 sm:col-span-3"><Label className="text-xs">Mode</Label><Pick value={p.mode} onChange={(v) => upd(i, "mode", v)} testid={`pay-mode-${i}`} options={modes.map((m) => ({ value: m, label: m }))} /></div>
          <div className="col-span-6 sm:col-span-3"><Label className="text-xs">Montant</Label><Input type="number" className="mt-1" value={p.montant} onChange={(e) => upd(i, "montant", Number(e.target.value))} data-testid={`pay-amount-${i}`} /></div>
          {ELEC.includes(p.mode) && <>
            <div className="col-span-6 sm:col-span-3"><Label className="text-xs">Téléphone</Label><Input className="mt-1" value={p.telephone || ""} onChange={(e) => upd(i, "telephone", e.target.value)} data-testid={`pay-phone-${i}`} /></div>
            <div className="col-span-6 sm:col-span-3"><Label className="text-xs">Réf. transaction</Label><Input className="mt-1" value={p.reference || ""} onChange={(e) => upd(i, "reference", e.target.value)} data-testid={`pay-ref-${i}`} /></div>
          </>}
        </div>
      ))}
      <Button type="button" variant="outline" size="sm" onClick={() => setPays([...pays, { mode: "TMoney", montant: 0 }])} data-testid="pay-add"><Plus className="h-4 w-4 mr-1" />Paiement</Button>
    </div>
  );
}

export default function SaleDialog({ tour, open, onOpenChange, onDone, canForce, presetClientId }) {
  const [clients, setClients] = useState([]);
  const [settings, setSettings] = useState({ pay_modes: [] });
  const [clientId, setClientId] = useState("");
  const [lines, setLines] = useState([]);
  const [pays, setPays] = useState([]);
  const [force, setForce] = useState(false);
  useEffect(() => {
    if (!open) return;
    setClientId(presetClientId || ""); setLines([{ article_id: "", quantite: 1, remise: 0 }]); setPays([{ mode: "Espèces", montant: 0 }]); setForce(false);
    cachedGet("/itinerant/clients").then(setClients).catch(() => {});
    cachedGet("/itinerant/settings").then(setSettings).catch(() => {});
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  const stock = tour?.mobile_stock || [];
  const total = lines.reduce((s, l) => { const r = stock.find((x) => x.article_id === l.article_id); return s + (r ? r.prix * l.quantite * (1 - (l.remise || 0) / 100) : 0); }, 0);
  const paid = pays.reduce((s, p) => s + (Number(p.montant) || 0), 0);
  const client = clients.find((c) => c.id === clientId);
  const submit = async () => {
    try {
      const r = await runOp("sale", tour.id, { client_id: clientId, lines: lines.filter((l) => l.article_id), payments: pays, force_credit: force }, client?.nom || "");
      toast.success(r.queued ? "Vente enregistrée hors ligne : elle sera synchronisée au retour du réseau" : "Vente enregistrée");
      onOpenChange(false); onDone();
    } catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="sale-dialog">
        <DialogHeader><DialogTitle className="font-head">Nouvelle vente</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div><Label className="text-xs">Client / boutique</Label>
            <Pick value={clientId} onChange={setClientId} placeholder="Choisir le client" testid="sale-client" options={clients.map((c) => ({ value: c.id, label: `${c.nom}${c.telephone ? " · " + c.telephone : ""}` }))} />
            {client && <p className="text-[11px] text-slate-500 mt-1" data-testid="sale-client-credit">Plafond {money(client.plafond_credit)} · encours {money(client.encours)} · disponible {money(client.credit_disponible)}</p>}
          </div>
          <SaleLines lines={lines} setLines={setLines} stock={stock} />
          <div>
            <div className="flex justify-between text-sm mb-1"><span className="font-semibold">Total</span><span className="font-mono font-bold text-brand-700" data-testid="sale-total">{money(total)}</span></div>
            <Payments pays={pays} setPays={setPays} modes={(settings.pay_modes || []).filter((m) => m !== "Crédit")} />
            <p className="text-xs mt-2">Encaissé {money(paid)} · <span className={total - paid > 0 ? "text-orange-600 font-semibold" : ""} data-testid="sale-credit">Crédit {money(Math.max(0, total - paid))}</span></p>
            {canForce && total - paid > 0 && <label className="flex items-center gap-2 text-xs mt-1"><input type="checkbox" checked={force} onChange={(e) => setForce(e.target.checked)} data-testid="sale-force-credit" />Autoriser le dépassement du plafond (responsable)</label>}
          </div>
        </div>
        <DialogFooter><Button variant="outline" onClick={() => onOpenChange(false)}>Annuler</Button>
          <Button onClick={submit} disabled={!clientId || !total} className="bg-brand-600 hover:bg-brand-700 text-white" data-testid="sale-submit">Valider la vente</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export { Pick };
